import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Users, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", label: "", price_inr: "", price_usd: "", max_courses: "", features: "", hidden: false };
const lines = (v) => (Array.isArray(v) ? v : String(v || "").split("\n")).map((s) => s.trim()).filter(Boolean);
const maxVal = (v) => (v === "" || v == null ? null : Number(v));

const PlanCard = ({ p, setField, save, del }) => (
  <div className={`rounded-xl border bg-white p-5 ${p.hidden ? "border-dashed border-slate-300 opacity-80" : "border-slate-200"}`} data-testid={`plan-edit-${p.name}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-500">{p.name}</span>
      <div className="flex items-center gap-1.5">
        {p.hidden && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><EyeOff className="h-3 w-3" />Hidden</span>}
        {p.custom && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Custom</span>}
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800" data-testid={`plan-count-${p.name}`}><Users className="h-3 w-3" />{p.institution_count ?? 0}</span>
      </div>
    </div>
    <div className="mt-4 space-y-3">
      <div><Label>Display Name</Label><Input value={p.label || ""} onChange={(e) => setField(p.id, "label", e.target.value)} placeholder={p.name} data-testid={`plan-label-${p.name}`} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Price INR (₹/yr)</Label><Input type="number" value={p.price_inr} onChange={(e) => setField(p.id, "price_inr", e.target.value)} disabled={p.name === "free"} data-testid={`plan-inr-${p.name}`} /></div>
        <div><Label>Price USD ($/yr)</Label><Input type="number" value={p.price_usd} onChange={(e) => setField(p.id, "price_usd", e.target.value)} disabled={p.name === "free"} data-testid={`plan-usd-${p.name}`} /></div>
      </div>
      <div><Label>Number of Courses (blank = unlimited)</Label><Input type="number" value={p.max_courses ?? ""} onChange={(e) => setField(p.id, "max_courses", e.target.value)} data-testid={`plan-max-${p.name}`} /></div>
      <div><Label>Features (one per line)</Label><Textarea rows={4} value={Array.isArray(p.features) ? p.features.join("\n") : p.features || ""} onChange={(e) => setField(p.id, "features", e.target.value)} data-testid={`plan-features-${p.name}`} /></div>
      <label className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        Hide from Pricing page
        <Switch checked={!!p.hidden} onCheckedChange={(v) => setField(p.id, "hidden", v)} data-testid={`plan-hidden-${p.name}`} />
      </label>
      <div className="flex gap-2">
        <Button className="flex-1 bg-blue-800 hover:bg-blue-900" size="sm" onClick={() => save(p)} data-testid={`save-plan-${p.name}`}>Save</Button>
        <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => del(p)} data-testid={`delete-plan-${p.name}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  </div>
);

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [np, setNp] = useState(EMPTY);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (id, k, v) => setPlans((ps) => ps.map((p) => p.id === id ? { ...p, [k]: v } : p));
  const setNew = (k, v) => setNp((n) => ({ ...n, [k]: v }));

  const save = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, {
        label: p.label || "", price_inr: Number(p.price_inr || 0), price_usd: Number(p.price_usd || 0),
        max_courses: maxVal(p.max_courses), features: lines(p.features), hidden: !!p.hidden,
      });
      toast.success(`${p.label || p.name} plan updated`); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    if (!np.name || !np.label) { toast.error("Plan ID and display name are required"); return; }
    try {
      await api.post("/admin/plans", {
        name: np.name, label: np.label, price_inr: Number(np.price_inr || 0), price_usd: Number(np.price_usd || 0),
        max_courses: maxVal(np.max_courses), features: lines(np.features), hidden: np.hidden,
      });
      toast.success("Plan created"); setNp(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (p) => {
    const n = p.institution_count || 0;
    const warn = n && p.name !== "free" ? `\n\n${n} institution(s) on this plan will be moved to the Free plan.` : "";
    if (!window.confirm(`Delete the "${p.label || p.name}" plan? This cannot be undone.${warn}`)) return;
    try {
      const { data } = await api.delete(`/admin/plans/${p.id}`);
      toast.success(data.moved_to_free ? `Plan deleted. ${data.moved_to_free} institution(s) moved to Free` : "Plan deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="mb-4 font-heading text-2xl font-bold tracking-tight text-slate-900">Listing Plans</h1>
      <p className="mb-6 text-sm text-slate-500">Add, edit or delete plans. Visible plans appear on the public Pricing page and in institution upgrade options.</p>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {plans.map((p) => <PlanCard key={p.id} p={p} setField={setField} save={save} del={del} />)}
      </div>

      <div className="mt-8 max-w-2xl rounded-xl border border-slate-200 bg-white p-5" data-testid="create-plan-form">
        <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">Add New Plan</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Plan ID</Label><Input value={np.name} onChange={(e) => setNew("name", e.target.value)} placeholder="e.g. enterprise" data-testid="new-plan-name" /></div>
          <div><Label>Display Name</Label><Input value={np.label} onChange={(e) => setNew("label", e.target.value)} placeholder="e.g. Enterprise" data-testid="new-plan-label" /></div>
          <div><Label>Price INR (₹/year)</Label><Input type="number" value={np.price_inr} onChange={(e) => setNew("price_inr", e.target.value)} data-testid="new-plan-inr" /></div>
          <div><Label>Price USD ($/year)</Label><Input type="number" value={np.price_usd} onChange={(e) => setNew("price_usd", e.target.value)} data-testid="new-plan-usd" /></div>
          <div><Label>Number of Courses</Label><Input type="number" value={np.max_courses} onChange={(e) => setNew("max_courses", e.target.value)} placeholder="blank = unlimited" data-testid="new-plan-max" /></div>
          <label className="flex items-center justify-between self-end rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            Hide from Pricing page <Switch checked={np.hidden} onCheckedChange={(v) => setNew("hidden", v)} data-testid="new-plan-hidden" />
          </label>
          <div className="sm:col-span-2"><Label>Features (one per line, optional)</Label><Textarea rows={3} value={np.features} onChange={(e) => setNew("features", e.target.value)} data-testid="new-plan-features" /></div>
        </div>
        <Button className="mt-4 bg-blue-800 hover:bg-blue-900" onClick={create} data-testid="create-plan-btn"><Plus className="mr-1.5 h-4 w-4" /> Create Plan</Button>
      </div>
    </div>
  );
}
